import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { useDisplayModeFields } from '../../hooks/useDisplayModeFields';
import FormFieldView from '../FormFieldView/FormFieldView';
import { getDocumentRequiredMessage } from '@utils/validation/document/requiredMessage';
import { UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentAbook } from '@models/Forms/IForms';
import { getDisplayValue, parseDisplayFormat } from '@utils/documentUtils';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import { IFieldElem } from '@models/IFormData';
import { getEmptyFormValueDependencies } from '@molecules/formbuilder/utils/emptyFormValueDependencies';
import { Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { FormMessageReserveContext } from '../../FormMessageReserveContext';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import {
    useChangeHandlersOptional,
    useRegisterFieldChanges,
    useReportChangeError,
    useRunFieldChanges,
} from '@controls/ChangeHandlers/useChangeHandlers';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { IPartialFormGroup } from '@srm/ui/controls/types';
import AbookChipItem from '@molecules/formbuilder/controls/Abook/AbookChipItem/AbookChipItem';
import UserName from '@molecules/UserName';
import AbookpickerControl from '@controls/AbookpickerControl';
import {
    useActivated,
    useClearErrorOnOptional,
    useFieldSaver,
    useFormulaDisplay,
    useFormulaState,
    useFormulaStateWithStatus,
    useLateVisibilityCascadeGate,
} from '@molecules/formbuilder/hooks';
import { extractFirstColumnName, processSetValues } from '@molecules/formbuilder/utils/setValuesUtils';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { useFormAbookTabs } from './useFormAbookTabs';

/**
 * Просмотр Abook: пары (ФИО, ключ адресной книги) в порядке выбора — для hover-карточек.
 * Ключи лежат в самом key-поле абука (fields[abook.key].value = "k1|k2|…", порядок выбора),
 * а ФИО — в отдельном display-поле, на которое ссылается displayFormat. Сопоставление по
 * индексу надёжно ТОЛЬКО когда весь displayFormat — это ровно один плейсхолдер {display-поле}
 * обычного (не табличного) абука и число имён совпадает с числом ключей. Иначе (eval, таблица,
 * мультиполя/литералы, рассинхрон количества) → null, и вызывающий код рендерит как раньше, без hover.
 */
function buildAbookViewPairs(
    abook: IDocumentAbook | undefined,
    fields: Record<string, IFieldElem>,
): { name: string; key: string }[] | null {
    const displayFormat = abook?.displayFormat;
    if (!displayFormat || abook?.evalDisplayFormat || abook?.source?.table || !abook?.key) return null;

    const placeholders = Array.from(displayFormat.matchAll(/\{.*?\}/g));
    if (placeholders.length !== 1 || displayFormat.trim() !== placeholders[0][0]) return null;

    const keys = String(fields[abook.key]?.value ?? '')
        .split('|')
        .map((k) => k.trim())
        .filter(Boolean);
    if (keys.length === 0) return null;

    const displayFieldName = placeholders[0][0].slice(1, -1); // убираем { }
    const names = String(fields[displayFieldName]?.value ?? '').split('|');
    if (names.length !== keys.length) return null;

    return names.map((name, i) => ({ name, key: keys[i] }));
}

export interface IAbookProps<TFieldValues extends object = object> {
    abook?: IDocumentAbook;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const Abook = <TFieldValues extends object = object>({
    abook,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    ensureExternalDataSourceLoaded,
}: IAbookProps<TFieldValues>) => {
    const viewFields = useDisplayModeFields(isEdit || isNew ? abook?.displayMode : undefined, fields, formMethods);
    const dataField = fields[abook?.key!];
    const runtime = useContext(DocumentRuntimeContext);
    const idField = dataField?.index;
    const { activated } = useActivated();
    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const reserveMessage = useContext(FormMessageReserveContext);
    const { value: visibilityFromFormula, settled: visibilityFromFormulaSettled } = useFormulaStateWithStatus(
        abook?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: abook
                ? {
                      targetId: abook.id || abook.key,
                      targetKind: 'field',
                      targetName: abook.name || abook.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const { value: requiredFromFormula, settled: requiredFromFormulaSettled } = useFormulaStateWithStatus(
        abook?.required,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: false,
            ensureExternalDataSourceLoaded,
            trace: abook
                ? {
                      targetId: abook.id || abook.key,
                      targetKind: 'field',
                      targetName: abook.name || abook.key,
                      ruleKind: 'required',
                  }
                : undefined,
        },
    );
    const readOnlyFromFormula = useFormulaState(abook?.readonly, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: abook
            ? {
                  targetId: abook.id || abook.key,
                  targetKind: 'field',
                  targetName: abook.name || abook.key,
                  ruleKind: 'readonly',
              }
            : undefined,
    });
    // Дебаг-оверрайды поля (жучок): forced-значения правил мимо формул.
    const fieldOverride = abook?.key ? fieldOverrides.get(abook.key) : undefined;
    const visibility = fieldOverride?.visibility ?? visibilityFromFormula;
    const visibilitySettled = fieldOverride?.visibility !== undefined || visibilityFromFormulaSettled;
    const readOnly = fieldOverride?.readonly ?? readOnlyFromFormula;
    const required = fieldOverride?.required ?? requiredFromFormula;
    const requiredSettled = fieldOverride?.required !== undefined || requiredFromFormulaSettled;
    const onSaveField = useFieldSaver(fields, formMethods);
    const runFieldChanges = useRunFieldChanges();
    const changeHandlers = useChangeHandlersOptional();
    const reportChangeError = useReportChangeError();
    const fieldBinding = useMemo(() => ({ key: abook?.key ?? '' }), [abook?.key]);
    useRegisterFieldChanges(fieldBinding, abook?.changes!, abook?.key ?? '', fields, isEdit, isNew, onSaveField, setError, formMethods, undefined, Boolean(abook && visibility));
    const skipCentralizedCascade = useLateVisibilityCascadeGate(abook, isNew, isEdit, visibility, visibilitySettled);

    const display = useFormulaDisplay(abook?.displayFormat, fields, formMethods, isEdit, isNew, activated, {
        fallback: dataField?.value,
        evalDisplayFormat: abook?.evalDisplayFormat,
        postProcess: (value: string) => {
            return getDisplayValue(value ?? '');
        },
        ensureExternalDataSourceLoaded,
        trace: abook
            ? {
                  targetId: abook.id || abook.key,
                  targetKind: 'field',
                  targetName: abook.name || abook.key,
                  ruleKind: 'formula',
              }
            : undefined,
    });

    // Просмотр: ключи АК для hover-карточек (null → рендер как раньше, без hover).
    const viewPairs = useMemo(
        () =>
            ((!isEdit && !isNew) || abook?.displayMode === 'view') && runtime.mode !== 'supportPreview'
                ? buildAbookViewPairs(abook, viewFields)
                : null,
        [isEdit, isNew, runtime.mode, abook, viewFields],
    );

    useEffect(() => {
        if (idField !== undefined && !includedFields.includes(idField)) {
            includedFields.push(idField);
        }
    }, [idField, includedFields]);

    const runNewInitCascade = useLatest(() => {
        if (skipCentralizedCascade) return;
        if (isNew && abook?.invokeChangesOnNew && visibility && !turnOffInvokeChangesOnNew) {
            const curValue = fields[abook.key];
            runFieldChanges(
                abook?.changes!,
                curValue?.value,
                abook?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        }
    });
    // Смена callbacks/карты полей не является повторной инициализацией.
    useEffect(() => runNewInitCascade.current(), [isNew, visibility, turnOffInvokeChangesOnNew, skipCentralizedCascade, runNewInitCascade]);

    useEffect(() => {
        const resetDisplayValue = async () => {
            if (!display.manager || !abook?.displayFormat) return;
            const fname = ('fields.[' + idField + '].value') as any;
            const val = formMethods.getValues(fname);
            let displayData = abook.displayFormat;
            if (displayData?.indexOf(abook?.key!) !== -1) {
                if (abook?.source?.table) {
                    const col = extractFirstColumnName(displayData);
                    if (col) {
                        displayData = (val as any[]).map((item: any) => item[col]).join(', ');
                    }
                } else {
                    const fieldVal = '{' + abook?.key + '}';
                    const source = new RegExp(fieldVal.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
                    displayData = displayData.replace(source, val as any);
                }
            }
            displayData = parseDisplayFormat(
                displayData,
                fields,
                getJoinSeparator(abook?.multilineDisplayOnView),
                abook?.evalDisplayFormat,
            ).text;
            if (activated.current) {
                display.setValue(getDisplayValue(displayData ?? ''));
            }
        };
        resetDisplayValue();
    }, [isNew, isEdit]);

    const getAbookAttrValue = useCallback((item: IAddressBookData, attr: string) => {
        type ObjectKey = keyof IAddressBookData;
        return item[attr as ObjectKey];
    }, []);

    const onSetValues = useCallback(
        async (value: IAddressBookData[]) => {
            let displayData: Awaited<ReturnType<typeof processSetValues>>;
            try {
                displayData = await processSetValues({
                    setValues: abook?.setValues,
                    fields,
                    formMethods,
                    isEdit,
                    isNew,
                    value,
                    getAttrValue: getAbookAttrValue,
                    displayFormat: abook?.displayFormat,
                    fieldKey: abook?.key!,
                    idField: idField!,
                    hasAddRows: false,
                    hasSourceTable: !!abook?.source?.table,
                    invokeSavedChange: async (binding, savedValue, rowData, activeTargets = new Set(), rowOldData) => {
                        return changeHandlers?.runSavedChanges(
                            binding,
                            savedValue,
                            rowData,
                            rowOldData,
                            { activeTargets },
                        ) ?? false;
                    },
                });
            } catch (error) {
                // корень каскада setValues: цели invokeChanges ошибку пробрасывают сюда
                reportChangeError(error, setError);
                return;
            }
            display.setValue(
                getDisplayValue(
                    parseDisplayFormat(
                        displayData,
                        fields,
                        getJoinSeparator(abook?.multilineDisplayOnView),
                        abook?.evalDisplayFormat,
                    ).text ?? '',
                ),
            );
        },
        [
            abook?.setValues,
            abook?.displayFormat,
            abook?.key,
            abook?.source?.table,
            fields,
            formMethods,
            isEdit,
            isNew,
            idField,
            getAbookAttrValue,
            changeHandlers,
            reportChangeError,
            setError,
        ],
    );

    useClearErrorOnOptional(required, requiredSettled, formMethods, dataField);
    const pickerTabs = isEdit || isNew ? abook?.tabs : undefined;
    const tabsSettings = useFormAbookTabs(pickerTabs, fields, formMethods, isEdit, isNew);

    const getFormValues = () => {
        let formDataParams = abook?.formValues;
        const map = {} as Record<string, any>;
        if (formDataParams) {
            for (const ele of formDataParams.formValue) {
                let field = fields[ele.attr];
                if (field === undefined) throw new Error(`Поле ${ele.attr} не найдено в перечне полей formValues`);
                let fname = ('fields.[' + field.index + '].value') as any;
                let val = formMethods.getValues(fname);
                if (ele.column) {
                    if (ele.function === '{join}') {
                        map[ele.key] = (val as [])?.map((item: any) => `'${item[ele.column]}'`).join(',') ?? val;
                    } else {
                        map[ele.key] = (val as [])?.map((item: any) => item[ele.column]) ?? val;
                    }
                } else {
                    map[ele.key] = val;
                }
            }
        }
        return JSON.stringify(map);
    };

    const getEmptyDependencies = () => getEmptyFormValueDependencies(abook?.formValues?.formValue, fields, formMethods);

    /**
     * Возвращает сепаратор для склеивания элементов
     * @param multilineDisplay Флаг отображения элементов в новой строке
     * @returns
     */
    const getJoinSeparator = (multilineDisplay?: boolean) => {
        return multilineDisplay ? '\r\n' : ', ';
    };

    const onChangeDictValue = async (value?: any) => {
        await runFieldChanges(
            abook?.changes!,
            value,
            abook?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
            fieldBinding,
        );
    };

    const effectiveRequired =
        debugSettings.skipAllValidation || debugSettings.skipRequiredValidation ? false : required;

    const renderView = () => {
        if (!abook) return null;
        const viewDisplay = viewPairs
            ? { text: '', values: [] }
            : parseDisplayFormat(
                  abook.displayFormat,
                  viewFields,
                  getJoinSeparator(abook.multilineDisplayOnView),
                  abook.evalDisplayFormat,
              );
        return (
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={viewPairs ? viewPairs.length === 0 : isEmptyDisplayValue(viewDisplay.text)}
                inLineFormat={abook.isValue}
                label={abook.name}
                wrapNewLines={abook?.multilineDisplayOnView}
                title={abook.viewTitle}
                verticalBottomAlign={abook?.verticalBottomAlign}
            >
                {abook.chipsView ? (
                    <div className="abook-chips-items">
                        {viewPairs
                            ? // сортируем по имени (визуальный паритет с .values.sort()), ключ остаётся при имени
                              [...viewPairs]
                                  .sort((a, b) => {
                                      const ta = a.name.toUpperCase();
                                      const tb = b.name.toUpperCase();
                                      return ta < tb ? -1 : ta > tb ? 1 : 0;
                                  })
                                  .map((pair, index) => (
                                      <AbookChipItem
                                          key={index}
                                          item={pair.name}
                                          userKey={pair.key}
                                          showRemoveIcon={false}
                                      />
                                  ))
                            : viewDisplay.values.map((item: string, index: number) => (
                                  <AbookChipItem
                                      key={index}
                                      item={item}
                                      showRemoveIcon={false}
                                      /*onRemove={() => {}}*/
                                  />
                              ))}
                    </div>
                ) : viewPairs ? (
                    // текст-режим: имена в порядке выбора (как .text); слева — мини-аватар
                    // (avatar) или инициалы, само имя — триггер hover-карточки.
                    viewPairs.map((pair, index) => (
                        <Fragment key={index}>
                            {index > 0 ? getJoinSeparator(abook?.multilineDisplayOnView) : null}
                            <UserName userKey={pair.key} name={pair.name} avatar />
                        </Fragment>
                    ))
                ) : (
                    viewDisplay.text
                )}
            </FieldWrapper>
        );
    };

    return abook && abook.key && visibility && idField != undefined ? (
        <div
            className={clsx(
                'form-field',
                (isEdit || isNew) && abook.displayMode !== 'view' && readOnly && 'form-field--readonly',
            )}
            data-testid={abook.id ? `abook-${abook.id}` : undefined}
            data-document-field-index={dataField.index}
            {...formulaTraceAttrs(abook.id || abook.key, 'field')}
        >
            {debugSettings.showFieldFormulas && (
                <FieldFormulaTooltip
                    required={abook.required}
                    visibilityRules={abook.visibilityRules}
                    readonly={abook.readonly}
                    fieldKey={abook.key}
                    isViewMode={!isEdit && !isNew}
                    rhfPath={dataField?.name}
                    value={dataField?.value}
                    requiredResult={requiredFromFormula}
                    readonlyResult={readOnlyFromFormula}
                    visibilityResult={visibilityFromFormula}
                />
            )}
            {(isEdit || isNew) && abook.displayMode === 'view' ? (
                <FormFieldView
                    name={`fields.[${idField}].value`}
                    control={formMethods.control}
                    rules={{
                        required: { value: effectiveRequired, message: getDocumentRequiredMessage(abook, 'abook') },
                    }}
                >
                    {renderView}
                </FormFieldView>
            ) : isEdit || isNew ? (
                <AbookpickerControl
                    label={abook?.name}
                    tooltip={<ToolTipTitle title={abook?.title} />}
                    formGroupProps={
                        { verticalBottomAlign: abook?.verticalBottomAlign, reserveMessage } as IPartialFormGroup
                    }
                    isMultiple={abook?.isMultiple}
                    recursive={abook?.recursive}
                    setValues={abook?.setValues}
                    externalSearch={abook?.externalSearch}
                    formValues={getFormValues()}
                    getFormValues={getFormValues}
                    title={abook?.name}
                    required={effectiveRequired}
                    rules={{
                        required: {
                            value: effectiveRequired ? effectiveRequired : false,
                            message: getDocumentRequiredMessage(abook ?? {}, 'abook'),
                        },
                    }}
                    readOnly={readOnly}
                    displayValue={display.value}
                    gridAttribute={abook?.gridAttribute}
                    name={`fields.[${idField}].value`}
                    onSetValues={onSetValues}
                    onAbookValueChanged={onChangeDictValue}
                    control={formMethods.control}
                    tabsSettings={tabsSettings}
                    source={abook?.source}
                    showChips={abook?.showChips}
                    placeholder={abook?.placeholder ? abook.placeholder : undefined}
                    wordWrapEnabled={abook?.wordWrapEnabled}
                    docId={docId}
                    showActionsBar={abook?.showActionsBar}
                    isSelectMode={abook?.isSelectMode}
                    getEmptyDependencies={getEmptyDependencies}
                />
            ) : (
                renderView()
            )}
        </div>
    ) : (
        <></>
    );
};

export default Abook;
